import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { DatePicker, Select } from "antd";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
    GetReporteAtenciones,
    GetReporteAtencionesExcel,
    GetReporteAtencionesPdf,
} from "../../Service/Service.reporteCoberturas";
import { GetTokenDecoded } from "../../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../../Service/Services.seguros";
import { ListarEspecialidadClinicaActivas } from "../../Service/Service.especialidad";
import { GetExamenesAll } from "../../Service/Service.examen";
import { GetPacientesAll } from "../../Service/Service.paciente";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../Components/UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../Components/UI/Chip";
import Boton from "../../Components/UI/Boton";
import BotonesExportar from "../../Components/UI/BotonesExportar";
import { useToast } from "../../Components/UI/Aviso";
import { IconoBuscar } from "../../Components/UI/Iconos";
import { usePageSubtitulo } from "../../context/PageHeaderContext";

const { RangePicker } = DatePicker;

// COBERTURAS-06 -- Reporte General de Atenciones y Estado de Cobertura.
// Vista ANALÍTICA (solo lectura) -- distinta del módulo operativo
// /factura-listar-por-cobrar. Filtros SIEMPRE VISIBLES (misma regla UX de 09/04).
//
// SISTEMA-DE-DISENO · pantalla migrada al kit (6ª). Primera de los reportes.
// La consulta, los filtros y los dos endpoints de exportación son los mismos;
// lo que cambia es la tabla: de 13 columnas con 1700px de ancho mínimo a 7 que
// caben en pantalla, sin perder un solo dato.
const TIPO_COBERTURA = [
    { value: "PARTICULAR", label: "Particular" },
    { value: "SEGURO_100", label: "Cobertura 100%" },
    { value: "COPAGO", label: "Copago" },
    { value: "NO_RESUELTA", label: "No resuelta" },
];
const ESTADO_SEGURO = [
    { value: "NO_APLICA", label: "No aplica" },
    { value: "SIN_DISTRIBUIR", label: "Sin distribuir" },
    { value: "PENDIENTE_FACTURAR", label: "Pendiente de facturar" },
    { value: "FACTURADO", label: "Facturado" },
    { value: "CANCELADO", label: "Cancelado" },
];
// El color pasa a significar lo mismo que en el resto del sistema: gris lo que
// no ha empezado, ámbar lo que está a medias, verde lo cerrado, rojo lo
// anulado. Antes eran cinco colores de antd sin criterio común.
const TONO_ESTADO = {
    NO_APLICA: "pendiente",
    SIN_DISTRIBUIR: "proceso",
    PENDIENTE_FACTURAR: "proceso",
    FACTURADO: "hecho",
    CANCELADO: "anulado",
};
const EMPTY = { rangoFechas: null, aseguradora: undefined, especialidad: undefined, paciente: undefined, examen: undefined, medico: undefined, tipo_cobertura: undefined, estado_responsabilidad_seguro: undefined };

const descargarBlob = (respuesta, nombre) => {
    const url = URL.createObjectURL(respuesta.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
};

function ReporteCoberturas() {
    const [draft, setDraft] = useState(EMPTY);
    const [applied, setApplied] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ prestaciones: 0, valor_servicio: 0, valor_paciente: 0, valor_aseguradora: 0 });

    const [seguros, setSeguros] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [examenes, setExamenes] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const { mostrar } = useToast();

    useEffect(() => {
        (async () => {
            const idclinica = GetTokenDecoded()?.idclinica;
            const seg = await ControllerListarSegurosClinica(idclinica);
            setSeguros((seg || []).map((s) => ({ value: s.idseguro, label: s.nombre })));
            const esp = await ListarEspecialidadClinicaActivas(idclinica);
            const espData = esp?.data || esp || [];
            setEspecialidades((Array.isArray(espData) ? espData : []).map((e) => ({ value: e.idespecialidad, label: e.especialidad })));
        })();
    }, []);

    const buscarExamenes = async (search) => {
        const r = await GetExamenesAll(1, 15, search || "");
        if (r?.success) setExamenes(r.data.map((d) => ({ value: d.IdExamenTipo, label: `${d.codigo} - ${d.descripcion}` })));
    };
    const buscarPacientes = async (search) => {
        const r = await GetPacientesAll(1, 15, search || "");
        if (r?.success) setPacientes(r.data.map((d) => ({ value: d.idpaciente, label: `${d.cedula} - ${d.nombres_completos}` })));
    };

    const fetchData = useCallback(async (p = 1, ps = 20, filters = {}) => {
        setLoading(true);
        setError(null);
        const res = await GetReporteAtenciones({ page: p, limit: ps, filters });
        if (res?.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idprocedimiento_cita_paciente })));
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.limit || ps);
            setSummary(res.summary || { prestaciones: 0, valor_servicio: 0, valor_paciente: 0, valor_aseguradora: 0 });
        } else {
            // ESTADO DE ERROR: antes la tabla se vaciaba en silencio y era
            // indistinguible de "no hay prestaciones en este rango".
            setRows([]);
            setTotal(0);
            setError(res?.message || "No se pudo obtener el reporte de atenciones.");
        }
        setLoading(false);
    }, []);

    const aplicar = () => {
        const [d0, d1] = draft.rangoFechas || [];
        const f = {
            fecha_inicial: d0 ? d0.format("YYYY-MM-DD") : undefined,
            fecha_final: d1 ? d1.format("YYYY-MM-DD") : undefined,
            aseguradora: draft.aseguradora,
            especialidad: draft.especialidad,
            paciente: draft.paciente,
            examen: draft.examen,
            medico: draft.medico,
            tipo_cobertura: draft.tipo_cobertura,
            estado_responsabilidad_seguro: draft.estado_responsabilidad_seguro,
        };
        setApplied(f);
        fetchData(1, pageSize, f);
    };
    const limpiar = () => {
        setDraft(EMPTY);
        setApplied({});
        fetchData(1, pageSize, {});
    };

    useEffect(() => {
        fetchData(1, pageSize, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    usePageSubtitulo(loading ? "" : `${total} prestacion${total === 1 ? "" : "es"}`);

    const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

    const columnas = [
        {
            // Paciente y fecha se leen juntos.
            key: "paciente",
            titulo: "Atención",
            render: (r) => (
                <CeldaDoble
                    principal={r.paciente ? tituloDeNombre(r.paciente) : "Sin determinar"}
                    secundario={r.fecha_emision ? format(new Date(r.fecha_emision), "dd/MM/yyyy", { locale: es }) : null}
                />
            ),
        },
        {
            // Servicio con su código, y la especialidad debajo.
            key: "servicio",
            titulo: "Servicio",
            render: (r) => (
                <CeldaDoble
                    principal={r.servicio_codigo ? `${r.servicio_codigo} - ${r.servicio}` : r.servicio}
                    secundario={r.especialidad}
                />
            ),
        },
        { key: "medico", titulo: "Médico", ancho: 180, render: (r) => <Celda valor={r.medico} /> },
        {
            // Aseguradora y el tipo de cobertura de esa prestación.
            key: "seguro",
            titulo: "Cobertura",
            ancho: 190,
            render: (r) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <ChipCategoria tono={r.tipo_cobertura === "NO_RESUELTA" ? undefined : "info"} incompleto={r.tipo_cobertura === "NO_RESUELTA"}>
                        {TIPO_COBERTURA.find((x) => x.value === r.tipo_cobertura)?.label || r.tipo_cobertura}
                    </ChipCategoria>
                    <span className="dt-doble__secundario">{r.seguro || "Sin aseguradora"}</span>
                </div>
            ),
        },
        {
            // Los tres importes en una celda: el del servicio manda y debajo va
            // el reparto entre paciente y seguro.
            key: "valor_servicio",
            titulo: "Importe",
            ancho: 190,
            alinear: "derecha",
            render: (r) => (
                <CeldaDoble
                    principal={money(r.valor_servicio)}
                    secundario={`Pac ${money(r.valor_paciente)} · Seg ${money(r.valor_aseguradora)}`}
                    monoPrincipal
                />
            ),
        },
        {
            key: "estado_responsabilidad_seguro",
            titulo: "Estado seguro",
            ancho: 180,
            render: (r) => (
                <ChipEstado tono={TONO_ESTADO[r.estado_responsabilidad_seguro] || "pendiente"}>
                    {ESTADO_SEGURO.find((x) => x.value === r.estado_responsabilidad_seguro)?.label || r.estado_responsabilidad_seguro}
                </ChipEstado>
            ),
        },
        {
            // Las dos facturas, etiquetadas: sin etiqueta, dos números sueltos
            // no se distinguen.
            key: "facturas",
            titulo: "Facturas",
            ancho: 190,
            render: (r) => (
                <CeldaDoble
                    principal={r.fac_paciente?.num_documento ? `Pac ${r.fac_paciente.num_documento}` : null}
                    secundario={r.fac_aseguradora?.num_documento ? `Seg ${r.fac_aseguradora.num_documento}` : null}
                    monoPrincipal
                    monoSecundario
                />
            ),
        },
    ];

    const hayFiltros = Object.values(applied).some((v) => v !== undefined && v !== null && v !== "");

    return (
        <div className="page-content">
            <div className="container-fluid">
                {/* ---- FILTROS SIEMPRE VISIBLES ---- */}
                <Row className="g-2 align-items-end mb-3 p-2 border rounded">
                    <Col xs={12} md={4} lg={3}>
                        <label className="kc__etiqueta">Rango de fechas</label>
                        <RangePicker className="w-100" value={draft.rangoFechas} onChange={(d) => setDraft((f) => ({ ...f, rangoFechas: d }))} getPopupContainer={() => document.body} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Aseguradora</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={seguros} value={draft.aseguradora} onChange={(v) => setDraft((f) => ({ ...f, aseguradora: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Especialidad</label>
                        <Select className="w-100" allowClear showSearch optionFilterProp="label" placeholder="Todas" options={especialidades} value={draft.especialidad} onChange={(v) => setDraft((f) => ({ ...f, especialidad: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Paciente</label>
                        <Select className="w-100" allowClear showSearch filterOption={false} placeholder="Buscar" options={pacientes} value={draft.paciente} onSearch={buscarPacientes} onChange={(v) => setDraft((f) => ({ ...f, paciente: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Servicio</label>
                        <Select className="w-100" allowClear showSearch filterOption={false} placeholder="Buscar" options={examenes} value={draft.examen} onSearch={buscarExamenes} onChange={(v) => setDraft((f) => ({ ...f, examen: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Tipo cobertura</label>
                        <Select className="w-100" allowClear options={TIPO_COBERTURA} value={draft.tipo_cobertura} onChange={(v) => setDraft((f) => ({ ...f, tipo_cobertura: v }))} />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta">Estado seguro</label>
                        <Select className="w-100" allowClear options={ESTADO_SEGURO} value={draft.estado_responsabilidad_seguro} onChange={(v) => setDraft((f) => ({ ...f, estado_responsabilidad_seguro: v }))} />
                    </Col>
                    <Col xs={12} className="d-flex gap-2 mt-2 flex-wrap">
                        <Boton variante="primario" icono={IconoBuscar} onClick={aplicar} data-testid="reporte-buscar">
                            Buscar
                        </Boton>
                        <Boton onClick={limpiar} data-testid="reporte-limpiar">
                            Limpiar
                        </Boton>
                        {/* El archivo lo genera el BACKEND (llega como blob), así que
                            se le pasan los descargadores. El permiso es el mismo
                            para todos los reportes. */}
                        <BotonesExportar
                            permiso="reportes.export"
                            titulo="Reporte General de Atenciones"
                            idPrefijo="reporte"
                            onExcel={async () => {
                                try {
                                    descargarBlob(await GetReporteAtencionesExcel({ filters: applied }), "REPORTE_GENERAL_ATENCIONES.xlsx");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el Excel." });
                                }
                            }}
                            onPdf={async () => {
                                try {
                                    descargarBlob(await GetReporteAtencionesPdf({ filters: applied }), "REPORTE_GENERAL_ATENCIONES.pdf");
                                } catch (e) {
                                    mostrar({ tipo: "error", texto: e?.message || "No se pudo generar el PDF." });
                                }
                            }}
                        />
                    </Col>
                </Row>

                {/* Totales del filtro aplicado, en el lenguaje del sistema. */}
                <div className="ac-filtros-linea mb-3" data-testid="reporte-totales">
                    {[
                        { etiqueta: "Prestaciones", valor: summary.prestaciones },
                        { etiqueta: "Valor servicio", valor: money(summary.valor_servicio) },
                        { etiqueta: "Valor paciente", valor: money(summary.valor_paciente) },
                        { etiqueta: "Valor aseguradora", valor: money(summary.valor_aseguradora) },
                    ].map((t) => (
                        <div className="ac-panel" key={t.etiqueta} style={{ flex: "1 1 180px", padding: "10px 14px", gap: 2 }}>
                            <span className="ac-etiqueta">{t.etiqueta}</span>
                            <span style={{ fontSize: 18, fontWeight: 600 }}>{t.valor}</span>
                        </div>
                    ))}
                </div>

                <DataTable
                    columnas={columnas}
                    filas={rows}
                    filaKey="idprocedimiento_cita_paciente"
                    cargando={loading}
                    error={error}
                    onReintentar={() => fetchData(page, pageSize, applied)}
                    etiquetaTabla="Reporte general de atenciones"
                    hayFiltrosAplicados={hayFiltros}
                    onLimpiarFiltros={limpiar}
                    textoVacioSinFiltros="No hay prestaciones registradas todavía."
                    paginacion={{
                        page,
                        pageSize,
                        total,
                        tamanos: [20, 50, 100],
                        onCambio: (p, ps) => fetchData(p, ps, applied),
                    }}
                />
            </div>
        </div>
    );
}

export default ReporteCoberturas;
